import type { Schemas } from '@/api/client'
import { registerAuthBridge } from '@/api/core'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

type User = Schemas['User']
type Organization = Schemas['Organization']
type TokenPair = Schemas['TokenPair']

// SEC-1: the refresh token now lives in an HttpOnly cookie posted by the
// API, and the access token is held in memory only. Neither is persisted
// to localStorage, so an XSS payload cannot exfiltrate credentials. The
// `user` / `org` / `permissions` projection is still persisted so the
// shell can render immediately on reload while the boot-time refresh
// races a new access token into memory.
interface AuthState {
  user: User | null
  org: Organization | null
  permissions: string[]
  accessToken: string | null
  isAuthenticated: boolean

  setAuth: (user: User, org: Organization, permissions: string[], tokens: TokenPair) => void
  setTokens: (tokens: TokenPair) => void
  setPermissions: (permissions: string[]) => void
  setOrg: (org: Organization) => void
  setUser: (user: User) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      org: null,
      permissions: [],
      accessToken: null,
      isAuthenticated: false,

      setAuth: (user, org, permissions, tokens) =>
        set({
          user,
          org,
          permissions,
          accessToken: tokens.access_token,
          isAuthenticated: true,
        }),

      setTokens: (tokens) =>
        set({
          accessToken: tokens.access_token,
        }),

      setPermissions: (permissions) => set({ permissions }),

      setOrg: (org) => set({ org }),

      setUser: (user) => set({ user }),

      clearAuth: () =>
        set({
          user: null,
          org: null,
          permissions: [],
          accessToken: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: 'amanet-auth',
      storage: createJSONStorage(() => localStorage),
      // Do NOT persist accessToken — it's in-memory only (SEC-1).
      partialize: (state) => ({
        user: state.user,
        org: state.org,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)

registerAuthBridge({
  getAccessToken: () => useAuthStore.getState().accessToken,
  setTokens: (tokens) => useAuthStore.getState().setTokens(tokens),
  clear: () => useAuthStore.getState().clearAuth(),
})
